import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import './ConnectionProfile.css';
import {
  User,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  Heart,
  MessageCircle,
  Users,
  Eye,
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  Share2,
  Bookmark,
  UserPlus,
  Mail,
  MoreVertical
} from 'lucide-react';
import axios from 'axios';

export default function ConnectionProfile() {
  const { userid } = useParams();
  const location = useLocation();
  const mongoId = location.state?.mongoId
  const navigate = useNavigate();
  const emptyProfile = {
    userid: "",
    name: "",
    headline: "",
    about: "",
    location: "",
    profilepic: "",
    contactInfo: {},
    experience: [],
    education: [],
    skills: [],
    connections: 0,
    profileViews: 0,
  };
  const [profile, setProfile] = useState(emptyProfile);
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");
  const [expandedPosts, setExpandedPosts] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [postStats, setPostStats] = useState({
    totalLikes: 0,
    totalComments: 0,
    totalPosts: 0
  });  
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        console.log('Fetching profile for user:', userid);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/profile/${userid}`
        );
        console.log('Profile data received:', res.data);
        setProfile(res.data);
        if (currentUser?.userid !== userid) {
          checkConnectionStatus(userid);
        }
      } catch (error) {
        console.error("Profile fetch error:", error);
        setProfile(emptyProfile);
      } finally {
        setLoading(false);
      }
    };
    if (userid) {
      fetchProfile();
    }
  }, [userid]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      try {
        console.log('Fetching posts for user:', userid);
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/posts/user/${userid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Posts data received:', res.data);
        const userPosts = res.data.filter(post => 
          post.author?.userid === userid || post.author?._id === userid
        );
        console.log('Filtered user posts:', userPosts);
        setUserPosts(userPosts);
        const stats = userPosts.reduce((acc, post) => ({
          totalLikes: acc.totalLikes + (post.likes?.length || 0),
          totalComments: acc.totalComments + (post.comments?.length || 0),
          totalPosts: acc.totalPosts + 1
        }), { totalLikes: 0, totalComments: 0, totalPosts: 0 });
        console.log('Post stats calculated:', stats);
        setPostStats(stats);
      } catch (error) {
        console.error("Posts fetch error:", error);
        setUserPosts([]);
      }
    };
    if (!loading && userid) {
      fetchUserPosts();
    }
  }, [userid, loading]);

  const checkConnectionStatus = async (userId) => {
    try {
      console.log('Checking connection status for:', userId);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/network/status/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Connection status:', res.data);
      setIsConnected(res.data.isConnected);
      setIsPending(res.data.isPending);
    } catch (error) {
      console.error("Error checking connection status:", error);
    }
  };

  const handleConnect = async () => {
    try {
      console.log('Sending connection request to:', userid);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/network/request/send/${userid}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Connection request sent successfully');
      setIsPending(true);
    } catch (error) {
      console.error("Error sending connection request:", error);
    }
  };

  const handleMessage = () => {
    console.log('Navigating to messages with:', userid);
    navigate(`/messages/${userid}`);
  };

  const toggleComments = (postId) => {
    console.log('Toggling comments for post:', postId);
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getProfileImageUrl = () => {
    if (profile.profilepic) {
      if (profile.profilepic.startsWith('http')) {
        return profile.profilepic;
      } else {
        console.log('Profile image URL:', `${process.env.REACT_APP_FILE_URL}${profile.profilepic}`);
        return `${process.env.REACT_APP_FILE_URL}${profile.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.userid || 'user'}`;
  };

  const getAuthorImageUrl = (author) => {
    if (author?.profilepic) {
      if (author.profilepic.startsWith('http')) {
        return author.profilepic;
      } else {
        return `${process.env.REACT_APP_FILE_URL}${author.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${author?.userid || 'user'}`;
  };

  const getCommenterImageUrl = (user) => {
    if (user?.profilepic) {
      if (user.profilepic.startsWith('http')) {
        return user.profilepic;
      } else {
        return `${process.env.REACT_APP_FILE_URL}${user.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.userid || 'commenter'}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <Loader2 size={48} className="cp-spin" />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="cp-container">
      <div className="cp-bg">
        <div className="cp-gradient"></div>
        <div className="cp-pattern"></div>
      </div>
      <div className="cp-header">
        <div className="cp-cover">
          <div className="cp-cover-gradient"></div>
        </div>
        <div className="cp-picture-section">
          <div className="cp-avatar-container">
            <img
              src={getProfileImageUrl()}
              className="cp-avatar"
              alt={profile.name}
              onError={(e) => {
                console.log('Image failed to load, using fallback');
                e.target.onerror = null;
                e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.userid || 'user'}`;
              }}
            />
          </div>
        </div>
        <div className="cp-info">
          <h1 className="cp-name">{profile.name || "Unnamed User"}</h1>
          <p className="cp-headline">{profile.headline || "No headline provided"}</p>
          {profile.location && (
            <div className="cp-location">
              <MapPin size={14} />
              <span>{profile.location}</span>
            </div>
          )}
          <div className="cp-action-buttons">
            {isConnected ? (
              <button className="cp-btn-connected" disabled>
                <Users size={16} />
                <span>Connected</span>
              </button>
            ) : isPending ? (
              <button className="cp-btn-pending" disabled>
                <UserPlus size={16} />
                <span>Pending</span>
              </button>
            ) : (
              <button className="cp-btn-primary" onClick={handleConnect}>
                <UserPlus size={16} />
                <span>Connect</span>
              </button>
            )}
            <button className="cp-btn-secondary" onClick={handleMessage}>
              <MessageCircle size={16} />
            </button>
            <button className="cp-btn-secondary">
              <Mail size={16} />
            </button>
          </div>
        </div>
      </div>
      <div className="cp-stats-grid">
        <div className="cp-stat-card">
          <div className="cp-stat-icon" style={{ color: '#ef4444' }}>
            <Heart size={24} />
          </div>
          <div className="cp-stat-content">
            <span className="cp-stat-value">{postStats.totalLikes}</span>
            <span className="cp-stat-label">Likes</span>
          </div>
        </div>
        <div className="cp-stat-card">
          <div className="cp-stat-icon" style={{ color: '#3b82f6' }}>
            <MessageCircle size={24} />
          </div>
          <div className="cp-stat-content">
            <span className="cp-stat-value">{postStats.totalComments}</span>
            <span className="cp-stat-label">Comments</span>
          </div>
        </div>        
        <div className="cp-stat-card">
          <div className="cp-stat-icon" style={{ color: '#10b981' }}>
            <Users size={24} />
          </div>
          <div className="cp-stat-content">
            <span className="cp-stat-value">{profile.connections || 0}</span>
            <span className="cp-stat-label">Connections</span>
          </div>
        </div>
        <div className="cp-stat-card">
          <div className="cp-stat-icon" style={{ color: '#8d65dd' }}>
            <Eye size={24} />
          </div>
          <div className="cp-stat-content">
            <span className="cp-stat-value">{profile.profileViews || 0}</span>
            <span className="cp-stat-label">Views</span>
          </div>
        </div>
      </div>
      <div className="cp-tabs">
        <button 
          className={`cp-tab-btn ${activeTab === "about" ? "active" : ""}`}
          onClick={() => setActiveTab("about")}
        >
          <User size={16} />
          <span>About</span>
        </button>
        <button 
          className={`cp-tab-btn ${activeTab === "experience" ? "active" : ""}`}
          onClick={() => setActiveTab("experience")}
        >
          <Briefcase size={16} />
          <span>Experience</span>
        </button>
        <button 
          className={`cp-tab-btn ${activeTab === "posts" ? "active" : ""}`}
          onClick={() => setActiveTab("posts")}
        >
          <MessageCircle size={16} />
          <span>Posts ({userPosts.length})</span>
        </button>
      </div>
      <div className="cp-tab-content">
        {activeTab === "about" && (
          <div className="cp-tab-pane">
            <div className="cp-info-card">
              <h3>About</h3>
              <p className="cp-about-text">
                {profile.about || "No bio provided."}
              </p>
            </div>
            <div className="cp-info-card">
              <h3>Skills</h3>
              {profile.skills?.length === 0 ? (
                <p className="cp-empty-text">No skills listed</p>
              ) : (
                <div className="cp-skills-grid">
                  {profile.skills?.map((skill, index) => (
                    <span key={index} className="cp-skill-tag">
                      <Award size={12} />
                      {skill.name || skill}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === "experience" && (
          <div className="cp-tab-pane">
            <div className="cp-info-card">
              <h3>Experience</h3>
              {profile.experience?.length === 0 ? (
                <p className="cp-empty-text">No experience added</p>
              ) : (
                <div className="cp-timeline">
                  {profile.experience?.map((exp, index) => (
                    <div key={index} className="cp-timeline-item">
                      <div className="cp-timeline-icon">
                        <Briefcase size={16} />
                      </div>
                      <div className="cp-timeline-content">
                        <h4>{exp.title}</h4>
                        <p className="cp-timeline-subtitle">{exp.company}</p>
                        <p className="cp-timeline-period">
                          <Calendar size={12} />
                          {exp.period}
                        </p>
                        {exp.description && (
                          <p className="cp-timeline-description">{exp.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>  
            <div className="cp-info-card">
              <h3>Education</h3>
              {profile.education?.length === 0 ? (
                <p className="cp-empty-text">No education added</p>
              ) : (
                <div className="cp-timeline">
                  {profile.education?.map((edu, index) => (
                    <div key={index} className="cp-timeline-item">
                      <div className="cp-timeline-icon">
                        <GraduationCap size={16} />
                      </div>
                      <div className="cp-timeline-content">
                        <h4>{edu.school}</h4>
                        <p className="cp-timeline-subtitle">{edu.degree}</p>
                        <p className="cp-timeline-period">
                          <Calendar size={12} />
                          {edu.period}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === "posts" && (
          <div className="cp-tab-pane">
            {userPosts.length === 0 ? (
              <div className="cp-empty-state">
                <MessageCircle size={48} className="cp-empty-icon" />
                <h3>No posts yet</h3>
                <p>This user hasn't posted anything yet.</p>
              </div>
            ) : (
              <>
                <div className="cp-posts-header">
                  <h3>Posts</h3>
                  <span className="cp-posts-count">{userPosts.length} total</span>
                </div>      
                <div className="cp-posts-feed">
                  {userPosts.map((post) => (
                    <div key={post._id} className="cp-post-card">
                      <div className="cp-post-header">
                        <div className="cp-post-author">
                          <img 
                            src={getAuthorImageUrl(post.author)}
                            className="cp-post-avatar"
                            alt={post.author?.name || "author"}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author?.userid || 'user'}`;
                            }}
                          />
                          <div className="cp-post-author-info">
                            <span className="cp-post-author-name">{post.author?.name || profile.name}</span>
                            <span className="cp-post-date">
                              <Calendar size={12} />
                              {post.createdAt ? formatDate(post.createdAt) : 'Recent'}
                            </span>
                          </div>
                        </div>
                        <div className="cp-post-actions">
                          <button className="cp-post-action-btn">
                            <Share2 size={16} />
                          </button>
                          <button className="cp-post-action-btn">
                            <Bookmark size={16} />
                          </button>
                          <button className="cp-post-action-btn">
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="cp-post-content">
                        <p className="cp-post-text">{post.content || post.text || "No content"}</p>
                      </div>
                      {post.image && (
                        <div className="cp-post-image">
                          <img 
                            src={post.image.startsWith('http') ? post.image : `${process.env.REACT_APP_FILE_URL}${post.image}`} 
                            alt="Post content"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="cp-post-stats">
                        <span className="cp-stat-item">
                          <Heart size={14} />
                          <span className="cp-stat-number">{post.likes?.length || 0}</span>
                          <span className="cp-stat-label">likes</span>
                        </span>
                        <span className="cp-stat-item">
                          <MessageCircle size={14} />
                          <span className="cp-stat-number">{post.comments?.length || 0}</span>
                          <span className="cp-stat-label">comments</span>
                        </span>
                        {post.views && (
                          <span className="cp-stat-item">
                            <Eye size={14} />
                            <span className="cp-stat-number">{post.views}</span>
                            <span className="cp-stat-label">views</span>
                          </span>
                        )}
                      </div>
                      {post.comments && post.comments.length > 0 && (
                        <div className="cp-post-comments">
                          <button 
                            className="cp-comments-toggle"
                            onClick={() => toggleComments(post._id)}
                          >
                            {expandedPosts[post._id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            <span>
                              {expandedPosts[post._id] ? 'Hide' : 'View'} {post.comments.length} comment{post.comments.length > 1 ? 's' : ''}
                            </span>
                          </button>
                          {expandedPosts[post._id] && (
                            <div className="cp-comments-list">
                              {post.comments.map((comment, idx) => (
                                <div key={idx} className="cp-comment-item">
                                  <img 
                                    src={getCommenterImageUrl(comment.user)}
                                    className="cp-comment-avatar"
                                    alt={comment.user?.name || "commenter"}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.user?.userid || 'commenter'}`;
                                    }}
                                  />
                                  <div className="cp-comment-content">
                                    <div className="cp-comment-header">
                                      <span className="cp-comment-author">
                                        {comment.user?.name || 'User'}
                                      </span>
                                      <span className="cp-comment-time">
                                        {comment.createdAt ? formatDate(comment.createdAt) : ''}
                                      </span>
                                    </div>
                                    <p className="cp-comment-text">{comment.text || comment.content || comment.message}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {post.comments?.length === 0 && (
                        <div className="cp-no-comments">
                          <MessageCircle size={14} />
                          <span>No comments yet</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}